import {
  CreditCard, Plus, Pencil, Trash2, Search, Eye,
  Image, Mail, MapPin, Phone, User,
  Briefcase, Building2, Layers, Monitor, FileText,
  Sparkles, Award, Package, TrendingUp, HeartHandshake, Target, Megaphone
} from 'lucide-react';
import type {
  DisplayFields, ContactInfo, CompanyIntro, CompanyQualifications,
  Cooperation, CompanyPromo, CardConfig
} from './types';

// ============ 默认电子名片背景图 ============
export const DEFAULT_BACKGROUND_LIST = [
   'https://www.cdkmzs.com/uploads/platform/0703/card_bg.png',
    'https://www.cdkmzs.com/uploads/platform/0703/card_bg2.png',
    'https://www.cdkmzs.com/uploads/platform/0703/card_bg3.png',
];

// ============ 页面内容选项（只保留企业简介、核心业务、企业愿景、企业理念） ============
export const pageContentOptions = [
  { value: 'companyIntro', label: '企业简介', labelEn: 'COMPANY PROFILE', icon: FileText },
  { value: 'coreBusiness', label: '核心业务', labelEn: 'CORE BUSINESS', icon: Briefcase },
  { value: 'companyVision', label: '企业愿景', labelEn: 'OUR VISION', icon: Target },
  { value: 'companyPhilosophy', label: '企业理念', labelEn: 'CORPORATE PHILOSOPHY', icon: Sparkles },
  { value: 'productIntro', label: '产品介绍', labelEn: 'PRODUCT INTRODUCTION', icon: Package },
  { value: 'companyPromo', label: '企业宣传', labelEn: 'COMPANY PROMO', icon: Megaphone },
];

// ============ 电子名片展示字段选项（去掉头像） ============
export const displayFieldOptions = [
  { value: 'name', label: '姓名', icon: User },
  { value: 'position', label: '职位', icon: Briefcase },
  { value: 'phone', label: '手机号', icon: Phone },
  { value: 'email', label: '邮箱', icon: Mail },
  { value: 'company', label: '公司名称', icon: Building2 },
  { value: 'companyAddress', label: '公司地址', icon: MapPin },
];

// ============ 名片布局选项 ============
export const cardLayouts = [
  { value: 'left', label: '布局一', desc: '左侧布局', preview: 'left' },
  { value: 'center', label: '布局二', desc: '居中布局', preview: 'center' },
  { value: 'right', label: '布局三', desc: '右侧布局', preview: 'right' },
  { value: 'preview-4', label: '布局四', desc: '预览四布局', preview: 'preview-4' },
];

// ============ 企业类型预设选项 ============
export const COMPANY_TYPE_OPTIONS = [
  '国有独资', '总部企业', '上市公司', '民营企业', '外资企业',
  '中外合资', '中外合作', '合伙企业', '个人独资企业', '有限责任公司',
  '股份有限公司', '集团企业', '其他'
];

// ============ 资质分类 ============
export const QUALIFICATION_CATEGORIES = [
  { value: 'honors', label: '企业荣誉', icon: Award },
  { value: 'businessQualifications', label: '经营资质', icon: FileText },
  { value: 'strength', label: '规模实力', icon: Building2 },
  { value: 'endorsement', label: '合作背书', icon: HeartHandshake },
];

// ============ 预设颜色 ============
export const presetColors = [
  '#1c5bff', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#6366f1', '#84cc16', '#f97316',
  '#64748b', '#334155', '#ffffff', '#000000'
];

// ============ Lucide 图标中文标签 ============
export const iconLabels: Record<string, string> = {
  menu: '菜单', x: '关闭', chevronLeft: '左箭头', chevronRight: '右箭头',
  chevronDown: '下箭头', chevronUp: '上箭头', arrowLeft: '向左', arrowRight: '向右',
  arrowUp: '向上', arrowDown: '向下', home: '首页', layout: '布局',
  grid: '网格', list: '列表', moreHorizontal: '更多', moreVertical: '更多',
  plus: '添加', minus: '减少', check: '确认', checkCircle: '确认圆圈',
  circle: '圆圈', trash: '删除', trash2: '删除2', edit: '编辑', pencil: '铅笔',
  copy: '复制', save: '保存', download: '下载', upload: '上传',
  search: '搜索', filter: '筛选', refresh: '刷新', undo: '撤销', redo: '重做',
  send: '发送', share: '分享', bookmark: '书签', flag: '标记', printer: '打印',
  link: '链接', externalLink: '外部链接', crop: '裁剪', move: '移动',
  lock: '锁定', unlock: '解锁', shield: '盾牌', shieldCheck: '盾牌确认',
  key: '密钥', eye: '眼睛', eyeOff: '眼睛关闭',
  settings: '设置', tool: '工具', wrench: '扳手', sliders: '滑块',
  toggleLeft: '左开关', toggleRight: '右开关', power: '电源',
  logIn: '登录', logOut: '登出',
  user: '用户', userPlus: '添加用户', userMinus: '减少用户',
  userCheck: '用户确认', userX: '用户删除', users: '多用户',
  group: '群组', idCard: '身份证',
  building: '建筑', briefcase: '公文包', store: '商店', cart: '购物车',
  shoppingCart: '购物车2', creditCard: '信用卡', wallet: '钱包',
  dollarSign: '美元', coin: '硬币', receipt: '收据',
  phone: '电话', mail: '邮件', message: '消息', messageCircle: '消息圈',
  bell: '铃铛',
  image: '图片', video: '视频', camera: '相机', mic: '麦克风',
  music: '音乐', play: '播放', pause: '暂停', volume: '音量',
  mapPin: '地图钉', map: '地图', clock: '时钟', calendar: '日历',
  timer: '计时器',
  file: '文件', fileText: '文本文件', folder: '文件夹', folderOpen: '打开文件夹',
  archive: '归档', book: '书籍', clipboard: '剪贴板',
  barChart2: '柱状图', barChart3: '柱状图3', lineChart: '折线图',
  pieChart: '饼图', trendingUp: '上涨', trendingDown: '下跌',
  alertCircle: '警告圈', alertTriangle: '警告三角', helpCircle: '帮助圈',
  info: '信息',
  star: '星星', heart: '心形', gift: '礼物', award: '奖项',
  medal: '奖牌', trophy: '奖杯', crown: '皇冠', sparkles: '闪光',
  rocket: '火箭', target: '目标', palette: '调色板', cloud: '云',
  sun: '太阳', moon: '月亮',
  facebook: 'Facebook', twitter: 'Twitter', github: 'GitHub',
  instagram: 'Instagram', youtube: 'YouTube', dribbble: 'Dribbble',
  figma: 'Figma',
  layers: '图层', type: '文字', barChart: '柱状图',
};

// ============ 默认值 ============
export const defaultDisplayFields: DisplayFields = {
  name: true, position: true, avatar: false, phone: true,
  email: true, company: true, companyAddress: true
};

export const defaultContact: ContactInfo = {
  qrcode: '', address: '', phone: '', email: ''
};

export const defaultCompanyIntro: CompanyIntro = {
  companyName: '',
  companyTypes: [],
  foundedDate: '',
  registeredCapital: '',
  creditRating: '',
  employeeCount: '',
  description: '',
  companyPositioning: [],
  businessSectors: []
};

export const defaultCompanyQualifications: CompanyQualifications = {
  honors: [],
  businessQualifications: [],
  strength: [],
  endorsement: []
};

export const defaultCooperation: Cooperation = {
  count: 0,
  logos: []
};

export const defaultCompanyPromo: CompanyPromo = {
  images: [],
  videos: []
};

export const defaultCard: Omit<CardConfig, 'id' | 'createdAt'> = {
  name: '',
  logo: '',
  pageBg: '',
  mainTitle: '',
  subTitle: '',
  subTitleEn: '',
  contact: { ...defaultContact },
  displayFields: { ...defaultDisplayFields },
  cardBgType: 'custom',
  cardBg: DEFAULT_BACKGROUND_LIST[0],
  cardLayout: 'left',
  status: 'active',
  saveToContacts: false,
  enablePoster: false,
  enableMiniCode: false,
  enableShortLink: false,
  selectedPages: [],
  philosophyItems: [],
  coreBusinessItems: [],
  productIntro: [],
  companyIntro: { ...defaultCompanyIntro },
  companyQualifications: { ...defaultCompanyQualifications },
  investmentProjectItems: [],
  companyStrengthItems: [],
  cooperation: { ...defaultCooperation },
  companyPromo: { images: [], videos: [] },
  visionItems: []
};

// ============ 初始卡片数据 ============
export const initialCards: CardConfig[] = [
  {
    ...defaultCard,
    id: 1,
    name: '企业电子名片',
    logo: 'https://www.cdkmzs.com/uploads/platform/0703/capital_logo.png',
    pageBg: 'https://www.cdkmzs.com/uploads/platform/0703/e-cardBg.png',
    mainTitle: '匠造建材 · 链通供需',
    subTitle: '金属建材全产业链智造、数字服务与供应链综合服务商',
    subTitleEn: 'FULL INDUSTRY CHAIN INTEGRATED SERVICE FOR METAL BUILDING MATERIALS',
    contact: {
      qrcode: 'https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=qr%20code&image_size=square',
      address: '北京市朝阳区科技园区88号',
      phone: '400-888-8888',
      email: 'contact@company.com'
    },
    displayFields: { name: true, position: true, avatar: false, phone: true, email: true, company: true, companyAddress: true },
    cardBgType: 'custom',
    cardBg: DEFAULT_BACKGROUND_LIST[0],
    cardLayout: 'left',
    status: 'active',
    createdAt: '2024-01-15',
    saveToContacts: false,
    enablePoster: false,
    posterBg: '',
    posterCardPosition: { x: 50, y: 50 },
    posterInvitePosition: { x: 50, y: 50 },
    enableMiniCode: false,
    enableShortLink: false,
    selectedPages: [],
    philosophyItems: [],
    coreBusinessItems: [],
    productIntro: [],
    promoVideo: ''
  }
];
